/**
 * 포인트/리워드 페이지 컴포넌트
 * 리팩토링: 컴포넌트 분리 및 모듈화
 * URL 기반 라우팅 적용
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  getPointsTranslations,
  PointsTabs,
  PointsTabContent,
  // #473 (R1): CouponsTabContent 파일 삭제 — '브랜드쿠폰' 탭 정리. 정상화 시점에 git history 에서 복원.
  PartnershipsTabContent,
} from './points';
import type { TabType, PointsData, PointsRewardsPageProps } from './points';
import type { Locale } from '@/config/locales';
import { useLanguage } from '@/contexts/AppContext';
import { useAuth } from '@/contexts/AuthContext';
import { pointsApi } from '@/lib/api/points';
import { usePointsPortfolio } from '@/hooks/usePointsPortfolio';
import type { PointAccount } from '@/lib/api/types';

// 탭-URL 매핑 테이블 (단일 소스)
// #473 (R1): brandCoupons 매핑 제거. ?tab=coupons 직접 진입 시 알 수 없는 키 → points fallback.
const TAB_URL_MAP: Record<TabType, string | undefined> = {
  points: undefined, // 기본값이므로 파라미터 생략
  partnerships: 'partnerships',
} as const;

// URL → TabType 역매핑 테이블
const URL_TAB_MAP: Record<string, TabType> = Object.fromEntries(
  Object.entries(TAB_URL_MAP)
    .filter(([, urlParam]) => urlParam !== undefined)
    .map(([tabType, urlParam]) => [urlParam, tabType as TabType])
) as Record<string, TabType>;

// URL 탭 파라미터를 TabType으로 변환
function parseTabFromUrl(tabParam: string | null): TabType {
  if (!tabParam) return 'points';
  return URL_TAB_MAP[tabParam] ?? 'points';
}

// TabType을 URL 탭 파라미터로 변환
function tabToUrlParam(tab: TabType): string | undefined {
  return TAB_URL_MAP[tab];
}

// BE 응답 → 로컬 PointsData 변환
const EMPTY_POINTS_DATA: PointsData = {
  totalPoints: 0,
  pointsEarned: 0,
  pointsUsed: 0,
  pointsExpiring: 0,
};

const portfolioLoadErrorText = {
  ko: '포인트 제휴 매장 정보를 불러오지 못했습니다.',
  es: 'No se pudo cargar la información de tiendas para tus puntos.',
  en: 'Could not load store information for your points.',
  pt: 'Não foi possível carregar as lojas dos seus pontos.',
  fr: 'Impossible de charger les magasins liés à vos points.',
  zh: '无法加载您的积分门店信息。',
  ja: 'ポイント対象店舗情報を読み込めませんでした。',
  hi: 'आपके अंकों के लिए स्टोर जानकारी लोड नहीं हो सकी।',
  de: 'Filialinformationen für deine Punkte konnten nicht geladen werden.',
  it: 'Impossibile caricare le informazioni dei negozi per i tuoi punti.',
  ru: 'Не удалось загрузить информацию о магазинах для ваших баллов.',
  ar: 'تعذر تحميل معلومات المتاجر الخاصة بنقاطك.',
  vi: 'Không thể tải thông tin cửa hàng cho điểm của bạn.',
  th: 'ไม่สามารถโหลดข้อมูลร้านค้าสำหรับคะแนนของคุณได้',
  nl: 'Kan winkelinformatie voor je punten niet laden.',
} satisfies Record<Locale, string>;

const retryText = {
  ko: '다시 시도',
  es: 'Reintentar',
  en: 'Retry',
  pt: 'Tentar novamente',
  fr: 'Réessayer',
  zh: '重试',
  ja: '再試行',
  hi: 'फिर कोशिश करें',
  de: 'Erneut versuchen',
  it: 'Riprova',
  ru: 'Повторить',
  ar: 'حاول مرة أخرى',
  vi: 'Thử lại',
  th: 'ลองอีกครั้ง',
  nl: 'Opnieuw proberen',
} satisfies Record<Locale, string>;

const noPointsText = {
  ko: '아직 사용 가능한 포인트가 없습니다',
  es: 'Aún no tienes puntos disponibles',
  en: 'No points available yet',
  pt: 'Ainda não há pontos disponíveis',
  fr: 'Aucun point disponible pour le moment',
  zh: '暂无可用积分',
  ja: 'まだ利用可能なポイントはありません',
  hi: 'अभी कोई उपलब्ध अंक नहीं हैं',
  de: 'Noch keine Punkte verfügbar',
  it: 'Non ci sono ancora punti disponibili',
  ru: 'Пока нет доступных баллов',
  ar: 'لا توجد نقاط متاحة بعد',
  vi: 'Chưa có điểm khả dụng',
  th: 'ยังไม่มีคะแนนที่ใช้ได้',
  nl: 'Nog geen punten beschikbaar',
} satisfies Record<Locale, string>;

function getLocaleText(locale: Locale, texts: Readonly<Record<Locale, string>>): string {
  return texts[locale];
}

export function PointsRewardsPage(_props: PointsRewardsPageProps) {
  const { locale } = useLanguage();
  const { isLoggedIn } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // 포인트 데이터 상태
  const [pointsData, setPointsData] = useState<PointsData>(EMPTY_POINTS_DATA);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [pointsError, setPointsError] = useState<string | null>(null);
  // #285 §5.4: accounts 존재 여부 (빈 상태 UI 분기용)
  const [pointsHasAccounts, setPointsHasAccounts] = useState(false);
  // #454: brand 단위 분리 표시용 — accounts 배열 보관 + 그룹핑
  const [pointsAccounts, setPointsAccounts] = useState<PointAccount[]>([]);
  const pointsPortfolio = usePointsPortfolio(pointsAccounts, isLoggedIn && pointsAccounts.length > 0);

  // 포인트 잔액 API 호출
  useEffect(() => {
    if (!isLoggedIn) {
      setPointsData(EMPTY_POINTS_DATA);
      setPointsLoading(false);
      setPointsError(null);
      setPointsHasAccounts(false);
      setPointsAccounts([]);
      return;
    }

    let cancelled = false;
    setPointsLoading(true);
    setPointsError(null);

    pointsApi.getBalance()
      .then((response) => {
        if (cancelled) return;
        if (!response.success || !response.data) {
          setPointsError('포인트 조회에 실패했습니다.');
          return;
        }
        const { total_balance, accounts } = response.data;
        const totalEarned = accounts.reduce((sum, a) => sum + a.lifetime_earned, 0);
        const totalUsed = accounts.reduce((sum, a) => sum + a.lifetime_used, 0);
        const totalExpiring = accounts.reduce((sum, a) => sum + a.expiring_soon.amount, 0);
        setPointsData({
          totalPoints: total_balance,
          pointsEarned: totalEarned,
          pointsUsed: totalUsed,
          pointsExpiring: totalExpiring,
        });
        setPointsHasAccounts(total_balance > 0 || accounts.length > 0);
        // #454: brand/store 분리 표시용 accounts 보관
        setPointsAccounts(accounts);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : '포인트 조회에 실패했습니다.';
        setPointsError(message);
      })
      .finally(() => {
        if (!cancelled) setPointsLoading(false);
      });

    return () => { cancelled = true; };
  }, [isLoggedIn]);

  // URL에서 탭 상태 파생
  const activeTab = useMemo(() => {
    return parseTabFromUrl(searchParams.get('tab'));
  }, [searchParams]);

  const t = getPointsTranslations(locale);
  const portfolioErrorMessage = pointsPortfolio.error ? getLocaleText(locale, portfolioLoadErrorText) : null;

  // 탭 변경 핸들러 (URL 네비게이션)
  const handleTabChange = useCallback((tab: TabType) => {
    const urlParam = tabToUrlParam(tab);
    const brandParam = searchParams.get('brand');
    const storeParam = searchParams.get('store');
    const nextParams: Record<string, string> = {};

    if (urlParam) {
      nextParams.tab = urlParam;
    }
    if (brandParam) {
      nextParams.brand = brandParam;
    }
    if (storeParam) {
      nextParams.store = storeParam;
    }

    setSearchParams(nextParams);
  }, [searchParams, setSearchParams]);

  // 메인 뷰 렌더링
  return (
    <div className="p-[14px] pb-20 bg-muted">
      {/* 탭 버튼 */}
      <PointsTabs activeTab={activeTab} onTabChange={handleTabChange} translations={t} />

      {/* 탭 컨텐츠 */}
      {activeTab === 'points' && (
        pointsLoading || pointsPortfolio.loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : pointsError || portfolioErrorMessage ? (
          <div className="bg-background rounded-[10px] p-6 text-center">
            <p className="text-destructive text-sm mb-2">{pointsError ?? portfolioErrorMessage}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-primary text-sm font-medium"
            >
              {getLocaleText(locale, retryText)}
            </button>
          </div>
        ) : !pointsHasAccounts ? (
          <div className="bg-background rounded-[10px] p-6 text-center">
            <p className="text-muted-foreground text-sm">
              {getLocaleText(locale, noPointsText)}
            </p>
          </div>
        ) : (
          <PointsTabContent
            pointsData={pointsData}
            translations={t}
            organizations={pointsPortfolio.organizations}
            highlightedBrandId={searchParams.get('brand')}
            highlightedStoreId={searchParams.get('store')}
          />
        )
      )}

      {/* #473 (R1): brandCoupons 탭 자체 제거. CouponsTabContent 파일/타입 일체 정리. */}

      {activeTab === 'partnerships' && <PartnershipsTabContent />}

    </div>
  );
}
